import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Inbox, Loader2, AlertTriangle, CheckCircle2, RefreshCw, Users, Link2 } from 'lucide-react';
import { STAGE_LABELS } from '../lib/stages';

const fmtTs = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const RESULT_CLS = {
  matched: 'text-emerald-300 border-emerald-700/50 bg-emerald-500/10',
  ai_match: 'text-emerald-300 border-emerald-700/50 bg-emerald-500/10',
  manual: 'text-emerald-300 border-emerald-700/50 bg-emerald-500/10',
  already_tracked: 'text-sky-300 border-sky-700/50 bg-sky-500/10',
  auto_reply: 'text-amber-300 border-amber-700/50 bg-amber-500/10',
  ndr: 'text-rose-300 border-rose-700/50 bg-rose-500/10',
  internal: 'text-muted-foreground border-border',
  ai_no_match: 'text-muted-foreground border-border',
  ai_checked_before: 'text-muted-foreground border-border',
  unmatched: 'text-muted-foreground border-border',
};

function MailboxRow({ mb, onRescan, busy }) {
  const stale = !mb.mail_read_ready || !!mb.last_error;
  return (
    <div className={`flex flex-wrap items-center gap-2 text-xs py-2 px-2 rounded-sm border ${stale ? 'border-rose-700/50 bg-rose-500/5' : 'border-border/60'}`}
      data-testid={`mailbox-scan-row-${mb.account}`}>
      {stale ? <AlertTriangle className="w-3.5 h-3.5 text-rose-400 shrink-0" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
      <span className="font-medium text-foreground truncate max-w-[260px]">{mb.account || '—'}</span>
      {mb.disconnected && <span className="text-[10px] uppercase tracking-wider text-rose-300 border border-rose-700/50 rounded-sm px-1.5 py-0.5" data-testid="mailbox-disconnected-badge">Verbindung verloren</span>}
      {mb.is_shared && (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-violet-300 border border-violet-700/50 rounded-sm px-1.5 py-0.5" data-testid="mailbox-shared-badge">
          <Users className="w-3 h-3" /> Sammelpostfach
        </span>
      )}
      {mb.user_name && <span className="text-muted-foreground">· {mb.user_name}</span>}
      <span className="ml-auto font-mono text-muted-foreground shrink-0" title="Letzter automatischer Scan">
        Scan: {fmtTs(mb.last_scanned_at || mb.last_run_at)}{mb.last_checked ? ` · ${mb.last_checked} Mails` : ''}
      </span>
      <button onClick={() => onRescan(mb)} disabled={busy || !mb.mail_read_ready}
        data-testid={`mailbox-rescan-btn-${mb.account}`}
        className="inline-flex items-center gap-1 text-[11px] text-sky-300 border border-sky-700/50 hover:bg-sky-500/10 disabled:opacity-40 rounded-sm px-2 py-1 transition-colors shrink-0">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} 7 Tage neu prüfen
      </button>
      {stale && (
        <div className="basis-full text-[11px] text-rose-300 pl-5" data-testid={`mailbox-scan-error-${mb.account}`}>
          {mb.last_error || 'Microsoft-Token abgelaufen — Postfach wird nicht gelesen. Als dieses Konto einloggen → Einstellungen → Microsoft neu verbinden.'}
        </div>
      )}
    </div>
  );
}

const ASSIGNABLE = new Set(['unmatched', 'ai_no_match', 'ai_checked_before', 'auto_reply']);

function DealPicker({ onPick, onCancel, busy }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      api.compareSearch(q.trim()).then((r) => setResults(r.data || [])).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="basis-full mt-1 mb-1 pl-2 sm:pl-[100px]" data-testid="rescan-deal-picker">
      <div className="flex items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus placeholder="Deal suchen (Firma, Projektname)…"
          data-testid="rescan-deal-search"
          className="flex-1 bg-secondary/40 border border-border rounded-sm px-2.5 h-8 text-xs text-foreground focus:outline-none focus:border-sky-700" />
        <button onClick={onCancel} className="text-[11px] text-muted-foreground hover:text-foreground" data-testid="rescan-deal-cancel">Abbrechen</button>
      </div>
      {q.trim().length >= 2 && (
        <div className="mt-1 border border-border rounded-sm max-h-48 overflow-y-auto bg-card">
          {results === null ? <div className="flex justify-center py-3"><Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" /></div>
            : results.length === 0 ? <p className="text-[11px] text-muted-foreground text-center py-3">Kein Deal gefunden.</p>
              : results.slice(0, 12).map((d, i) => (
                <button key={d.id} onClick={() => onPick(d)} disabled={busy} data-testid={`rescan-deal-result-${i}`}
                  className="w-full text-left px-2.5 py-2 hover:bg-secondary/50 border-b border-border/40 last:border-b-0 transition-colors disabled:opacity-50">
                  <p className="text-xs text-foreground font-medium truncate">{d.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{[d.random_project_name, STAGE_LABELS[d.pipeline_stage] || d.pipeline_stage].filter(Boolean).join(' · ')}</p>
                </button>
              ))}
        </div>
      )}
    </div>
  );
}

function ReportRow({ r, i, mailboxUserId, onAssigned }) {
  const navigate = useNavigate();
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const assign = async (deal) => {
    setBusy(true);
    try {
      const res = await api.inboxRescanAssign({
        user_id: mailboxUserId, deal_id: deal.id, from_email: r.from_email, from_name: r.from_name, subject: r.subject,
        ts: r.ts, message_id: r.message_id, internet_message_id: r.internet_message_id, snippet: r.snippet,
      });
      const d = res.data;
      toast.success(`${r.from_name || r.from_email} → ${d.deal_name}${d.stage_after !== d.stage_before ? ` · Deal jetzt in „${STAGE_LABELS[d.stage_after] || d.stage_after}“` : ''}${d.contact_created ? ' · Absender als Kontakt hinterlegt' : ''}`);
      onAssigned(i, { result: 'manual', label: 'Manuell zugeordnet → Deal', deal_id: d.deal_id, deal_name: d.deal_name, deal_stage: d.stage_after });
      setPicking(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Zuordnung fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 text-xs" data-testid={`rescan-row-${i}`}>
      <span className="font-mono text-muted-foreground shrink-0 w-[92px]">{fmtTs(r.ts)}</span>
      <span className="truncate max-w-[220px] text-foreground" title={r.from_email}>{r.from_name || r.from_email}</span>
      <span className="truncate flex-1 min-w-[120px] text-muted-foreground" title={r.subject}>{r.subject || '(kein Betreff)'}</span>
      <span className={`shrink-0 text-[10px] border rounded-sm px-1.5 py-0.5 ${RESULT_CLS[r.result] || RESULT_CLS.unmatched}`}>{r.label}</span>
      {r.deal_id && (
        <button onClick={() => navigate(`/projects/${r.deal_id}`)} className="shrink-0 text-sky-300 hover:underline truncate max-w-[160px]" title={`Stage: ${r.deal_stage}`}>
          {r.deal_name || 'Deal'}
        </button>
      )}
      {!r.deal_id && ASSIGNABLE.has(r.result) && !picking && (
        <button onClick={() => setPicking(true)} data-testid={`rescan-assign-btn-${i}`}
          className="shrink-0 inline-flex items-center gap-1 text-[11px] text-sky-300 border border-sky-700/50 hover:bg-sky-500/10 rounded-sm px-2 py-0.5 transition-colors">
          <Link2 className="w-3 h-3" /> Deal zuordnen
        </button>
      )}
      {picking && <DealPicker onPick={assign} onCancel={() => setPicking(false)} busy={busy} />}
    </div>
  );
}

function RescanReport({ res, onClose }) {
  const [rows, setRows] = useState(res.report);
  useEffect(() => { setRows(res.report); }, [res]);
  const onAssigned = (i, patch) => setRows((prev) => prev.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const found = rows.filter((r) => ['matched', 'ai_match', 'already_tracked', 'manual'].includes(r.result)).length;
  return (
    <div className="mt-3 border border-border rounded-sm" data-testid="mailbox-rescan-report">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border text-xs">
        <span className="text-foreground font-medium">{res.account}</span>
        <span className="text-muted-foreground">· {res.checked} Mails der letzten {res.days} Tage geprüft · {found} zu Deals · <span className={res.new_hints ? 'text-emerald-300' : ''}>{res.new_hints} neu erfasst</span></span>
        <button onClick={onClose} className="ml-auto text-[11px] text-muted-foreground hover:text-foreground" data-testid="mailbox-rescan-close">Schließen</button>
      </div>
      <p className="px-3 pt-2 text-[10px] text-muted-foreground">Gehört eine nicht erkannte Mail doch zu einem Deal? „Deal zuordnen" erfasst sie als Antwort und merkt sich den Absender für künftige Mails.</p>
      <div className="max-h-[360px] overflow-y-auto divide-y divide-border/50">
        {rows.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground">Keine Mails im Zeitraum.</div>}
        {rows.map((r, i) => <ReportRow key={r.internet_message_id || i} r={r} i={i} mailboxUserId={res.user_id} onAssigned={onAssigned} />)}
      </div>
    </div>
  );
}

export function MailboxScanCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [res, setRes] = useState(null);
  const load = useCallback(() => { api.getInboxScanStatus().then((r) => setData(r.data)).catch(() => setData({ mailboxes: [] })); }, []);
  useEffect(() => { load(); }, [load]);

  const rescan = async (mb) => {
    setBusy(mb.user_id);
    try {
      const r = await api.inboxRescan(mb.user_id, 7);
      setRes({ ...r.data, user_id: mb.user_id });
      if (r.data.new_hints) toast.success(`${r.data.new_hints} neue Antwort(en) aus ${mb.account} erfasst`);
      else toast.info(`${mb.account}: ${r.data.checked} Mails geprüft — nichts Neues`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Postfach-Scan fehlgeschlagen'); }
    finally { setBusy(''); }
  };

  if (!data) return null;
  const problems = data.mailboxes.filter((m) => !m.mail_read_ready || m.last_error);
  return (
    <div className="bg-card border border-border rounded-sm p-5" data-testid="mailbox-scan-section">
      <div className="flex items-center gap-2 mb-1">
        <Inbox className="w-4 h-4 text-sky-400" />
        <h2 className="text-sm font-medium text-foreground">Postfach-Scan — welche Postfächer die Antworten liefern</h2>
      </div>
      <p className="text-[10px] text-muted-foreground mb-3">
        Alle 10 Minuten werden diese Posteingänge auf Antworten kontaktierter Eigentümer geprüft (Absender-Adresse, Domain, KI-Signatur). Nur Deals in „Contacted" werden zugeordnet.
        {!data.shared_connected && <span className="text-rose-300"> {data.shared_mailbox} ist NICHT verbunden — bitte als dieses Konto einloggen und Microsoft verbinden.</span>}
      </p>
      {problems.length > 0 && (
        <div className="mb-2 text-[11px] text-rose-300 flex items-center gap-1.5" data-testid="mailbox-scan-warning">
          <AlertTriangle className="w-3.5 h-3.5" /> {problems.length} Postfach{problems.length > 1 ? 'er werden' : ' wird'} derzeit nicht gelesen — Antworten dort fehlen in den Statistiken.
        </div>
      )}
      <div className="space-y-1.5">
        {data.mailboxes.length === 0 && <div className="text-xs text-muted-foreground" data-testid="mailbox-scan-empty">Kein Microsoft-Postfach verbunden.</div>}
        {data.mailboxes.map((mb) => <MailboxRow key={mb.user_id} mb={mb} onRescan={rescan} busy={busy === mb.user_id} />)}
      </div>
      {res && <RescanReport res={res} onClose={() => setRes(null)} />}
    </div>
  );
}
